import React, { Suspense } from "react";
import { Navigate, Route, Routes, Outlet } from "react-router-dom";
import { Spinner } from "@shared/components/ui";
import { useFeature } from "@shared/lib/useFeature";
import { AuthTransferPage } from "@shared/components/AuthTransferPage";
import { MobileOnlyGate } from "@shared/components/MobileOnlyGate";
import { RequireAuth } from "@shared/components/RequireAuth";
import { MobileShell } from "./layouts/MobileShell";

// Lazy-loaded pages
const DashboardPage = React.lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const LogActivityPage = React.lazy(() => import("./pages/LogActivityPage").then(m => ({ default: m.LogActivityPage })));
const SchedulePage = React.lazy(() => import("./pages/SchedulePage").then(m => ({ default: m.SchedulePage })));
const ScheduleRequestsPage = React.lazy(() => import("./pages/ScheduleRequestsPage").then(m => ({ default: m.ScheduleRequestsPage })));
const SubmitScheduleRequestPage = React.lazy(() => import("./pages/SubmitScheduleRequestPage").then(m => ({ default: m.SubmitScheduleRequestPage })));
const SubmitTimeOffRequestPage = React.lazy(() => import("./pages/SubmitTimeOffRequestPage").then(m => ({ default: m.SubmitTimeOffRequestPage })));
const ClassroomDetailPage = React.lazy(() => import("./pages/ClassroomDetailPage").then(m => ({ default: m.ClassroomDetailPage })));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const MessagesPage = React.lazy(() => import("./pages/MessagesPage").then(m => ({ default: m.MessagesPage })));
const ChildProfilePage = React.lazy(() => import("./pages/ChildProfilePage").then(m => ({ default: m.ChildProfilePage })));

// Legal pages - lazy loaded (public, accessible pre-login)
const TermsPage = React.lazy(() => import("@shared/components/legal/TermsContent").then(m => ({ default: m.TermsContent })));
const PrivacyPage = React.lazy(() => import("@shared/components/legal/PrivacyContent").then(m => ({ default: m.PrivacyContent })));

// Auth pages (loaded synchronously)
import { LoginPage } from "./pages/LoginPage";

function PageLoader() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <Spinner label="Loading..." />
    </div>
  );
}

function MobileOnlyGateLayout() {
  return (
    <MobileOnlyGate>
      <Outlet />
    </MobileOnlyGate>
  );
}

function SubscriptionFeatureGate({ feature, children }: { feature: Parameters<typeof useFeature>[0], children: React.ReactNode }) {
  const isEnabled = useFeature(feature);
  if (!isEnabled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/auth-transfer" element={<AuthTransferPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />

      <Route element={<RequireAuth />}>
        <Route element={<MobileOnlyGateLayout />}>
          <Route element={<MobileShell />}>
            <Route path="/" element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
            <Route path="/log-activity" element={<Suspense fallback={<PageLoader />}><LogActivityPage /></Suspense>} />
            <Route path="/schedule" element={
              <SubscriptionFeatureGate feature="scheduling">
                <Suspense fallback={<PageLoader />}><SchedulePage /></Suspense>
              </SubscriptionFeatureGate>
            } />
            <Route path="/schedule/requests" element={
              <SubscriptionFeatureGate feature="scheduling">
                <Suspense fallback={<PageLoader />}><ScheduleRequestsPage /></Suspense>
              </SubscriptionFeatureGate>
            } />
            <Route path="/schedule/request/new" element={
              <SubscriptionFeatureGate feature="scheduling">
                <Suspense fallback={<PageLoader />}><SubmitScheduleRequestPage /></Suspense>
              </SubscriptionFeatureGate>
            } />
            <Route path="/schedule/time-off/new" element={
              <SubscriptionFeatureGate feature="scheduling">
                <Suspense fallback={<PageLoader />}><SubmitTimeOffRequestPage /></Suspense>
              </SubscriptionFeatureGate>
            } />
            <Route path="/my-classrooms/:id" element={<Suspense fallback={<PageLoader />}><ClassroomDetailPage /></Suspense>} />
            <Route path="/children/:id" element={<Suspense fallback={<PageLoader />}><ChildProfilePage /></Suspense>} />
            <Route path="/messages" element={<Suspense fallback={<PageLoader />}><MessagesPage /></Suspense>} />
            <Route path="/me" element={<Suspense fallback={<PageLoader />}><ProfilePage /></Suspense>} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
